import React, { useState, useRef, useCallback } from 'react';
import { twMerge } from 'tailwind-merge';
import ButtonComponent from 'components/button/buttonComponent';
import { ReactComponent as Up } from 'assets/icons/chevron-up.svg';
import { CalendarEvent } from 'calendar-link';
import { useAddToCalendar } from 'hooks/useAddToCalendar';
import { DropdownBodyContainer } from './DropdownBodyContainer';
import { ReactComponent as GoogleCalendarIcon } from 'assets/icons/google-calendar.svg';
import { ReactComponent as OutlookIcon } from 'assets/icons/outlook-icon.svg';
import useOutsideClick from '../datePicker/hooks/useOutsideClick';

interface CalendarDropdownProps {
  appointmentId: string;
  calendarEvent?: CalendarEvent | null;
}

export const CalendarDropdown: React.FC<CalendarDropdownProps> = ({
  appointmentId,
  calendarEvent,
}) => {
  const [opened, setOpened] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const closeOptions = useCallback(() => setOpened(false), []);
  useOutsideClick(dropdownRef, closeOptions, opened);

  const { addToGoogleCalendar, addToOutlookCalendar } = useAddToCalendar();

  const handleToggle = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setOpened((o) => !o);
  };

  return (
    <div
      className="relative w-full flex items-center justify-center"
      ref={dropdownRef}
      onMouseUp={handleToggle}
    >
      <ButtonComponent
        testID={`${appointmentId}-calendar-button`}
        className="!font-bold whitespace-nowrap"
        containerClassName="!gap-1"
      >
        <span>Add to</span>
        <Up
          className={twMerge(
            !opened && 'rotate-180',
            'group-hover:fill-clc-blue fill-white w-[12px] h-[6px] ml-1 transition-transform duration-300',
          )}
        />
      </ButtonComponent>

      {/* Dropdown */}
      <DropdownBodyContainer
        align="right"
        className={twMerge(
          'flex flex-col items-start border border-gray-opacity-15 rounded-5 p-2.5 gap-2.5 bg-white',
          !opened && 'hidden',
        )}
      >
        <ButtonComponent
          testID={`${appointmentId}-google-calendar-button`}
          onClick={(e) => {
            e.stopPropagation();
            addToGoogleCalendar(calendarEvent);
            setOpened(false);
          }}
          className="whitespace-nowrap py-2"
          type="underline"
          Icon={GoogleCalendarIcon}
          iconPosition={'left'}
          fill="fill-auto"
          stroke="stroke-auto"
        >
          Add to Google Calendar
        </ButtonComponent>

        <ButtonComponent
          testID={`${appointmentId}-outlook-calendar-button`}
          onClick={(e) => {
            e.stopPropagation();
            addToOutlookCalendar(calendarEvent);
            setOpened(false);
          }}
          className="whitespace-nowrap py-2"
          type="underline"
          Icon={OutlookIcon}
          iconPosition={'left'}
          fill="fill-auto"
          stroke="stroke-auto"
        >
          Add to Outlook Calendar
        </ButtonComponent>
      </DropdownBodyContainer>
    </div>
  );
};
